import React, { useState } from "react";
import { useDispatch } from "react-redux";
import {
    Box,
    Button,
    TextField,
    Typography,
    useTheme,
    useMediaQuery,
} from "@mui/material";

import TagManager from "./tagManager";
import LoadingSpinner from "./LoadingSpinner";
import { updatePrompt, createPrompt, deletePrompt } from "../services/apiService";
import { setLoading } from "../reducers/loadingReducer";
import { showToast } from "../reducers/uiReducer";

import CheckboxDropdown from "./CheckboxDropdown";
import InfoBubble from "./InfoBubble";

const PromptEditor = ({ prompt, onClose, isNewPrompt }) => {
    const theme = useTheme();
    const dispatch = useDispatch();
    const isMobile = useMediaQuery('(max-width:800px)');
    const [editedPrompt, setEditedPrompt] = useState(prompt);
    const [showConfirmDelete, setShowConfirmDelete] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState("");

    const handleSave = async () => {
        if (!editedPrompt.title || !editedPrompt.description || !editedPrompt.promptText) {
            setError("Title, Description, and Prompt Text are required.");
            return;
        }

        setIsSaving(true);
        dispatch(setLoading(true));
        try {
            if (isNewPrompt) {
                await createPrompt(editedPrompt, dispatch);
            } else {
                await updatePrompt(editedPrompt.id, editedPrompt, dispatch);
            }
            dispatch(showToast({ message: "Prompt saved successfully!", severity: "success" }));
            onClose();
        } catch (error) {
            console.error('Failed to save prompt:', error);
            dispatch(showToast({ message: "Failed to save prompt!", severity: "error" }));
        } finally {
            setIsSaving(false);
            dispatch(setLoading(false));
        }
    };

    const handleDelete = async () => {
        dispatch(setLoading(true));
        try {
            await deletePrompt(editedPrompt.id, dispatch);
            dispatch(showToast({ message: "Prompt deleted successfully!", severity: "success" }));
            onClose();
        } catch (error) {
            dispatch(showToast({ message: `Failed to delete prompt: ${error?.message}`, severity: "error" }));
        } finally {
            dispatch(setLoading(false));
        }
    };

    const handleTagsChange = (newTags) => {
        setEditedPrompt({ ...editedPrompt, tags: newTags });
    };

    const options = [
        { label: 'Code', value: 'Coding' },
        { label: 'Document', value: 'Document Review' },
        { label: 'Data', value: 'Data Analysis' },
        { label: 'General', value: 'General Purpose' },
    ];

    return (
        <Box sx={{ width: '100%' }}>
            <Box
                sx={{
                    border: `1px solid ${theme.palette.divider}`,
                    backgroundColor: theme.palette.background.paper,
                    padding: 2,
                    borderRadius: 2,
                    animation: 'fadeIn 0.4s forwards',
                }}
            >
                <Box sx={{ mb: 2 }}>
                    <TextField
                        label="Short Title"
                        value={editedPrompt.title}
                        onChange={(e) => setEditedPrompt({ ...editedPrompt, title: e.target.value })}
                        fullWidth
                        disabled={isSaving}
                        required
                    />
                </Box>
                <Box sx={{ mb: 2 }}>
                    <TextField
                        label="Description"
                        value={editedPrompt.description}
                        onChange={(e) => setEditedPrompt({ ...editedPrompt, description: e.target.value })}
                        fullWidth
                        disabled={isSaving}
                        required
                    />
                </Box>
                <Box sx={{ mb: 2 }}>
                    <TextField
                        label="Prompt"
                        value={editedPrompt.promptText}
                        onChange={(e) => setEditedPrompt({ ...editedPrompt, promptText: e.target.value })}
                        fullWidth
                        multiline
                        rows={4}
                        disabled={isSaving}
                        required
                    />
                </Box>

                <Box sx={{ display: isMobile ? 'block' : 'flex', gap: 2 }}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: 1,
                            height: 56,
                            paddingX: 2,
                            marginBottom: isMobile ? 2 : 0,
                        }}
                    >
                        <CheckboxDropdown options={options} disabled={isSaving} />
                        {/* <InfoBubble
                            message="Link your prompts to a specific AI Utility for faster access."
                            disabled={isSaving}
                        /> */}
                    </Box>
                    <TagManager
                        tags={editedPrompt.tags || []}
                        onChange={handleTagsChange}
                        disabled={isSaving}
                    />
                </Box>

                {error && (
                    <Typography sx={{ color: theme.palette.error.main, mt: 2 }}>
                        {error}
                    </Typography>
                )}
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
                <Box>
                    {!isNewPrompt &&
                        (showConfirmDelete ? (
                            <>
                                <Button
                                    variant="contained"
                                    color="error"
                                    onClick={handleDelete}
                                    sx={{ mr: 2 }}
                                    disabled={isSaving}
                                >
                                    Confirm Delete
                                </Button>
                                <Button
                                    onClick={() => setShowConfirmDelete(false)}
                                    disabled={isSaving}
                                >
                                    Cancel
                                </Button>
                            </>
                        ) : (
                            <Button
                                variant="outlined"
                                color="error"
                                onClick={() => setShowConfirmDelete(true)}
                                disabled={isSaving}
                            >
                                Delete Prompt
                            </Button>
                        ))}
                </Box>
                <Box>
                    {isSaving ? (
                        <LoadingSpinner />
                    ) : (
                        <>
                            <Button
                                variant="contained"
                                onClick={handleSave}
                                sx={{ mr: 2 }}
                                disabled={isSaving}
                            >
                                Save
                            </Button>
                            <Button onClick={onClose} disabled={isSaving}>
                                Cancel
                            </Button>
                        </>
                    )}
                </Box>
            </Box>
        </Box>
    );
};

export default PromptEditor;
